/**
 * Image optimization utility using wsrv.nl CDN
 * Automatically converts images to WebP and resizes them
 * Only works in production - returns original URL in development
 */

interface OptimizeOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'jpg' | 'png';
}

// Check if we're in production (published site)
const isProduction = typeof window !== 'undefined' && 
  !window.location.hostname.includes('localhost') &&
  !window.location.hostname.includes('lovableproject.com') &&
  !window.location.hostname.includes('preview');

/**
 * Get the full URL for a local asset
 */
function getFullUrl(src: string): string {
  // If it's already a full URL, return as-is
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }
  
  // For Vite-bundled assets, construct the full production URL
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  
  // Handle relative paths
  if (src.startsWith('/')) {
    return `${origin}${src}`;
  }
  
  return `${origin}/${src}`;
}

/**
 * Optimize an image URL using wsrv.nl CDN
 * @param src - The image source (can be imported asset or URL)
 * @param options - Optimization options
 * @returns Optimized image URL (or original in dev)
 */
export function optimizeImage(src: string, options: OptimizeOptions = {}): string {
  // Always optimize external URLs (Supabase storage images)
  // Only skip local assets in development/preview
  const isExternalUrl = src.startsWith('http://') || src.startsWith('https://');
  
  if (!isProduction && !isExternalUrl) {
    return src;
  }

  const {
    width,
    height,
    quality = 80,
    format = 'webp'
  } = options;

  const fullUrl = getFullUrl(src);
  
  // Build wsrv.nl URL
  const params = new URLSearchParams();
  params.set('url', fullUrl);
  
  if (width) params.set('w', width.toString());
  if (height) params.set('h', height.toString());
  params.set('q', quality.toString());
  params.set('output', format);
  
  // Enable smart cropping for better results
  if (width && height) {
    params.set('fit', 'cover');
  }

  return `https://wsrv.nl/?${params.toString()}`;
}

/**
 * Pre-configured sizes for common use cases
 */
export const imageSizes = {
  carCard: { width: 220, quality: 80 },
  carCardDesktop: { width: 400, quality: 80 },
  carCardMobile: { width: 220, quality: 75 },
  hero: { width: 800, quality: 85 },
  heroMobile: { width: 480, quality: 80 },
  thumbnail: { width: 150, quality: 70 },
} as const;
